import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Image, ArrowRight, SpinnerGap, Plus } from '@phosphor-icons/react';
import { jobPhotosAPI, jobsAPI } from '../api/api';

const PHOTO_TYPE_COLORS = {
  before: 'bg-amber-500',
  progress: 'bg-blue-500',
  after: 'bg-green-500',
  milestone: 'bg-purple-500'
};

const RecentJobPhotos = ({ userType = 'contractor' }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobsWithPhotos, setJobsWithPhotos] = useState([]);

  useEffect(() => {
    fetchRecentPhotos();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, []);

  const fetchRecentPhotos = async () => {
    try {
      setLoading(true);
      // First, get the user's jobs
      let jobIds = [];
      
      if (userType === 'contractor') {
        const jobsResponse = await jobsAPI.getMyJobs();
        jobIds = (jobsResponse.data.jobs || []).map(j => j._id);
      } else {
        // For workers, get jobs they're assigned to via applications
        const { applicationsAPI } = await import('../api/api');
        const appsResponse = await applicationsAPI.getMyApplications();
        const acceptedApps = (appsResponse.data.applications || []).filter(a => a.status === 'accepted');
        jobIds = acceptedApps.map(a => a.job_id);
      }

      // Fetch photos from each job (limit to recent 3 jobs)
      const allPhotos = [];
      const jobsInfo = [];
      
      for (const jobId of jobIds.slice(0, 3)) {
        try {
          const response = await jobPhotosAPI.getJobPhotos(jobId);
          if (response.data.success && response.data.photos.length > 0) {
            // Get job title from the first photo or fetch it
            const jobPhotos = response.data.photos.slice(0, 4);
            allPhotos.push(...jobPhotos.map(p => ({ ...p, job_id: jobId })));
            jobsInfo.push({ id: jobId, photoCount: response.data.count });
          }
        } catch (err) {
          // Skip jobs where we can't fetch photos
          console.log(`Skipping job ${jobId}`);
        }
      }

      // Sort by date and take recent 8
      allPhotos.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setPhotos(allPhotos.slice(0, 8));
      setJobsWithPhotos(jobsInfo);
    } catch (error) {
      console.error('Error fetching recent photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoClick = (photo) => {
    navigate(`/job/${photo.job_id}`);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2563EB]">
        <div className="flex items-center gap-2 mb-4">
          <Camera size={24} weight="bold" className="text-[#2B4C6F]" />
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Recent Job Photos</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <SpinnerGap size={32} weight="bold" className="animate-spin text-[#2B4C6F]" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2563EB]">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Camera size={24} weight="bold" className="text-[#2B4C6F]" />
          <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Recent Job Photos</h3>
        </div>
        {photos.length > 0 && (
          <button 
            onClick={() => navigate('/my-jobs')}
            className="flex items-center gap-1 text-[#2B4C6F] font-bold text-sm hover:underline"
          >
            View Jobs <ArrowRight size={16} weight="bold" />
          </button>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="text-center py-8">
          <Image size={48} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-3" />
          <p className="text-neutral-500 dark:text-neutral-400 mb-2">No photos yet</p>
          <p className="text-sm text-neutral-400 dark:text-neutral-500">
            {userType === 'contractor' 
              ? 'Photos from your jobs will appear here' 
              : 'Document your work with before/after photos'}
          </p>
          <button
            onClick={() => navigate('/my-jobs')}
            className="mt-4 flex items-center gap-2 mx-auto px-4 py-2 bg-[#2563EB] text-[#0F172A] font-bold rounded-lg border-2 border-black shadow-[2px_2px_0px_#000] hover:shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
          >
            <Plus size={18} weight="bold" />
            Go to Jobs
          </button>
        </div>
      ) : (
        <>
          {/* Photo Grid */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {photos.map((photo, idx) => (
              <div 
                key={photo._id}
                onClick={() => handlePhotoClick(photo)}
                className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2B4C6F] transition-all"
              >
                <img 
                  src={photo.image_url}
                  alt={photo.title || 'Job photo'}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                />
                {/* Type badge */}
                <div className={`absolute top-1 left-1 w-2 h-2 rounded-full ${PHOTO_TYPE_COLORS[photo.photo_type] || 'bg-blue-500'}`} />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white text-xs font-bold">View</span>
                </div>
              </div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center justify-between text-sm border-t-2 border-neutral-100 dark:border-neutral-700 pt-3">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-neutral-500 dark:text-neutral-400">Before</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-neutral-500 dark:text-neutral-400">Progress</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-neutral-500 dark:text-neutral-400">After</span>
              </div>
            </div>
            <span className="text-neutral-400 dark:text-neutral-500">
              {jobsWithPhotos.length} job{jobsWithPhotos.length !== 1 ? 's' : ''} with photos
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default RecentJobPhotos;
